import { useCallback, useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate, API } from "@/lib/api";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";
import { LedgerTrend } from "@/components/LedgerTrend";
import { StorageBadge } from "@/components/StorageBadge";
import { Input } from "@/components/ui/input";

const FILTERS = [["", "All"], ["unpaid", "Outstanding"], ["paid", "Collected"]];

export default function Registry() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/registry/ledger", { params: { q: q || undefined, status: status || undefined } });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const csv = `${API}/registry/ledger.csv${status ? `?status=${status}` : ""}`;

  return (
    <div data-testid="registry-page">
      <PageHeader
        eyebrow="Registry"
        title="Package Fee Ledger"
        description="Every AdhocArb case fee raised across the registry, half by half, with what has been collected and what is still due. The arbitrators' own fee notes are not included."
        testId="registry-header"
        actions={
          <a href={csv} data-testid="ledger-csv-button"
            className="inline-flex items-center h-10 px-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
            <Download className="h-3.5 w-3.5 mr-2" /> Download CSV
          </a>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ledger-totals">
        {[
          ["Collected", data?.totals.collected, "collected"],
          ["Outstanding", data?.totals.outstanding, "outstanding"],
          ["Collected this month", data?.totals.this_month, "this-month"],
        ].map(([label, value, id]) => (
          <div key={id} className="border border-border bg-card p-6">
            <p className="label-caps">{label}</p>
            <p className="font-display text-3xl mt-3" data-testid={`total-${id}`}>
              {value == null ? "—" : money(value)}
            </p>
          </div>
        ))}
        <div className="border border-border bg-card p-6">
          <p className="label-caps">References · fee notes</p>
          <p className="font-display text-3xl mt-3" data-testid="total-counts">
            {data ? `${data.totals.cases} · ${data.totals.notes}` : "—"}
          </p>
        </div>
      </div>

      <StorageBadge />

      <LedgerTrend />

      <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
        <div className="relative w-full sm:w-96">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ledger-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by file no., cause, party, counsel or fee note"
            className="rounded-none pl-9 focus:ring-2 focus:ring-blue-800" />
        </div>
        <div className="flex">
          {FILTERS.map(([v, l]) => (
            <button key={v || "all"} data-testid={`ledger-filter-${v || "all"}`} onClick={() => setStatus(v)}
              className={`px-4 h-10 text-xs uppercase tracking-widest border transition-colors duration-200 ${
                status === v ? "bg-slate-900 text-white border-slate-900" : "border-border hover:bg-accent"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="h-40 border border-border bg-muted animate-pulse mt-6" />
      ) : data.rows.length === 0 ? (
        <div className="mt-6">
          <EmptyState testId="ledger-empty" title="Nothing on the ledger"
            hint="Fee notes appear here as soon as references are filed." />
        </div>
      ) : (
        <div className="mt-6 border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {["Fee note", "Reference", "Cause", "Side", "Counsel", "Amount", "Status"].map((h) => (
                  <th key={h} className="p-4 text-left label-caps whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="ledger-rows">
              {data.rows.map((r) => (
                <tr key={r.invoice_id} className="border-b border-border last:border-0 hover:bg-accent/50 transition-colors duration-200"
                  data-testid={`ledger-row-${r.invoice_id}`}>
                  <td className="p-4 font-mono text-xs whitespace-nowrap">
                    {r.invoice_number}
                    <span className="block text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                      {r.kind === "subscription" ? "case fee" : "upgrade"} · {r.plan}
                    </span>
                  </td>
                  <td className="p-4 font-mono text-xs whitespace-nowrap">{r.case_number || r.case_ref}</td>
                  <td className="p-4 min-w-56">
                    {r.case_title}
                    <span className="block text-xs text-muted-foreground mt-1">{r.claimant} vs {r.respondent}</span>
                  </td>
                  <td className="p-4 capitalize whitespace-nowrap">{r.side}</td>
                  <td className="p-4 whitespace-nowrap">{r.claimant_advocate || "—"}</td>
                  <td className="p-4 font-mono whitespace-nowrap">{money(r.amount)}</td>
                  <td className="p-4 whitespace-nowrap">
                    <Badge className={r.status === "paid" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}
                      testId={`ledger-status-${r.invoice_id}`}>{r.status}</Badge>
                    {r.paid_at && (
                      <span className="block text-[11px] text-muted-foreground mt-1">{fmtDate(r.paid_at)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
